'use client';

import * as Sentry from '@sentry/react';
import { User as FirebaseUser } from 'firebase/auth';
import { ReactNode, createContext, useCallback, useEffect, useState } from 'react';

import { axiosClient } from '@/lib/api-client.js';

import { clearImpersonation } from '@/auth/impersonation.js';
import { useCsrf } from '@/lib/csrf-context.js';
import { type ApiTargetUser, type LoggedInUser } from '@airreading/common/data';

import { authFailureFromAirLoginError, type AuthFailure } from './auth-errors.js';
import { useFirebaseAuth } from './useFirebaseAuth.js';

export type IdentityProviderUser = FirebaseUser | null | undefined;

export interface AuthContext {
  /**
   * undefined: identity provider hasn't reported yet
   * null: user is not signed in
   * User: user signed in
   *
   */
  identityProviderUser: IdentityProviderUser;
  loggedInUser: LoggedInUser | undefined;
  actingUser: ApiTargetUser | undefined;
  authFailure: AuthFailure | undefined;

  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

export const AuthContext = createContext<AuthContext>({
  identityProviderUser: undefined,
  loggedInUser: undefined,
  actingUser: undefined,
  authFailure: undefined,
  signInWithGoogle: async () => {},
  logout: async () => {},
  loading: true,
});

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const {
    identityProviderUser,
    identityProviderFlow,
    providerFailure,
    clearProviderFailure,
    signInWithGoogle: providerSignInWithGoogle,
    logout: providerLogout,
  } = useFirebaseAuth();
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser | undefined>(undefined);
  const [authFailure, setAuthFailure] = useState<AuthFailure | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const { csrfToken, csrfError, refreshCsrfToken, clearCsrfToken } = useCsrf();

  const airLogin = useCallback(() => {
    return axiosClient.get<LoggedInUser>('/me/login');
  }, []);

  const logout = useCallback(async () => {
    setAuthFailure(undefined);
    clearProviderFailure();
    try {
      // Call backend logout endpoint to revoke session tokens
      await axiosClient.post('/me/logout');
    } catch (error) {
      Sentry.captureException(error);
      // Continue with client logout even if backend fails
    }

    // Clear local state
    clearImpersonation({ reload: false });
    clearCsrfToken();

    // Sign out from the identity provider (client-side)
    return providerLogout();
  }, [clearCsrfToken, clearProviderFailure, providerLogout]);

  useEffect(() => {
    if (!providerFailure) {
      return;
    }

    setAuthFailure(providerFailure);
    setLoading(false);
  }, [providerFailure]);

  useEffect(() => {
    if (!csrfError) {
      return;
    }

    setAuthFailure({
      code: 'auth_air_unavailable',
      email: identityProviderUser?.email,
      flow: identityProviderFlow,
      cause: csrfError,
    });
    setLoading(false);
  }, [csrfError, identityProviderFlow, identityProviderUser?.email]);

  useEffect(() => {
    if (csrfToken) {
      axiosClient.defaults.headers['x-csrf-token'] = csrfToken;
    }
  }, [csrfToken]);

  useEffect(() => {
    if (identityProviderUser === undefined) {
      return;
    }

    if (identityProviderUser) {
      void (async () => {
        let csrfRefreshed = false;
        try {
          await refreshCsrfToken();
          csrfRefreshed = true;
          const { data: user } = await airLogin();
          Sentry.setUser({ id: user.id });

          setAuthFailure(undefined);
          setLoggedInUser(user);
          setLoading(false);
        } catch (error: unknown) {
          setAuthFailure(
            csrfRefreshed
              ? authFailureFromAirLoginError(error, identityProviderUser.email, identityProviderFlow)
              : {
                  code: 'auth_air_unavailable',
                  email: identityProviderUser.email,
                  flow: identityProviderFlow,
                  cause: error,
                },
          );
          setLoggedInUser(undefined);
          setLoading(false);

          clearImpersonation({ reload: false });
          clearCsrfToken();
          await providerLogout().catch((logoutError: unknown) => Sentry.captureException(logoutError));
        }
      })();
    } else {
      setLoading(false);
      setLoggedInUser(undefined);
    }
  }, [identityProviderUser, identityProviderFlow, airLogin, refreshCsrfToken, clearCsrfToken, providerLogout]);

  const signInWithGoogle = async () => {
    setAuthFailure(undefined);
    clearProviderFailure();
    setLoading(true);
    try {
      const result = await providerSignInWithGoogle();
      if (result === 'cancelled' || result === 'failed') {
        setLoading(false);
      }
    } catch (error: unknown) {
      setAuthFailure({ code: 'auth_unknown_error', flow: 'google', cause: error });
      setLoading(false);
    }
  };

  const authValue = {
    identityProviderUser,
    loggedInUser,
    actingUser: loggedInUser?.impersonatingAs || loggedInUser,
    authFailure,
    signInWithGoogle,
    logout,
    loading,
  };

  return <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>;
};
